if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // Filter media gallery on load based on Color variant
        setTimeout(() => {
          if (window.ProductGalleryColorFilter) {
            window.ProductGalleryColorFilter.update();
          }
        }, 0);

        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.variant) {
            this.updateVariantDescription(event.data.variant);
          }
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        // Instantly update variant input and Add to Cart button state to avoid delay
        const matchedVariant = this.getMatchingVariantFromDOM(target);
        if (matchedVariant) {
          this.updateVariantInputs(matchedVariant.id);
          if (matchedVariant.available) {
            this.productForm?.toggleSubmitButton(false);
          } else {
            this.productForm?.toggleSubmitButton(true, window.variantStrings.soldOut);
          }

          // Instantly update the gallery to avoid delay
          if (window.ProductGalleryColorFilter) {
            window.ProductGalleryColorFilter.update(null, matchedVariant);
          }
          const mediaId = matchedVariant.featured_media?.id || matchedVariant.featured_image?.id;
          if (mediaId) {
            this.querySelector('media-gallery')?.setActiveMedia?.(
              `${this.dataset.section}-${mediaId}`,
              true
            );
          }
        } else {
          this.updateVariantInputs(null);
          this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        }

        let currentOptionValues = selectedOptionValues;
        const variantSelects = this.querySelector('variant-selects');
        if (variantSelects) {
          currentOptionValues = variantSelects.selectedOptionValues || selectedOptionValues;
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, currentOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getMatchingVariantFromDOM(changedInput = null) {
        const variantsScript = this.querySelector('variant-selects [data-product-variants]') ||
                               document.querySelector('variant-selects [data-product-variants]');
        if (!variantsScript) {
          console.log('[Variant Lookup] Reason if no variant is found: No variants script found.');
          return null;
        }

        try {
          const variants = JSON.parse(variantsScript.textContent);
          const optionContainers = Array.from(this.querySelectorAll('variant-selects .product-form__input'));
          if (!optionContainers.length) {
            console.log('[Variant Lookup] Reason if no variant is found: No option containers found.');
            return null;
          }

          const cleanVal = (val) => {
            if (!val) return '';
            const doc = new DOMParser().parseFromString(val, 'text/html');
            return doc.documentElement.textContent.trim().toLowerCase().replace(/\s+/g, ' ');
          };

          // 1. Get raw selected option values from the DOM
          const selectedOptionValues = optionContainers.map(container => {
            const input = container.querySelector('input:checked, select');
            if (!input) return '';
            if (input.tagName === 'SELECT') {
              return input.value;
            }
            return input.value || input.getAttribute('data-value') || '';
          });

          // 2. Try to find exact match
          let matchedVariant = variants.find(variant => {
            return variant.options.every((optValue, index) => {
              return cleanVal(selectedOptionValues[index]) === cleanVal(optValue);
            });
          });

          // 3. Auto-adjust selections if exact match not found
          if (!matchedVariant) {
            // Determine which option changed
            let changedOptionIndex = -1;
            if (changedInput) {
              changedOptionIndex = optionContainers.findIndex(container => container.contains(changedInput));
            }

            // Fallback: Default to first option
            if (changedOptionIndex === -1) {
              changedOptionIndex = 0;
            }

            const changedValue = selectedOptionValues[changedOptionIndex];
            const candidates = variants.filter(variant => {
              return cleanVal(variant.options[changedOptionIndex]) === cleanVal(changedValue);
            });

            if (candidates.length > 0) {
              let bestCandidate = null;
              let highestScore = -1;

              candidates.forEach(variant => {
                let score = 0;
                variant.options.forEach((opt, idx) => {
                  if (idx === changedOptionIndex) return;
                  if (cleanVal(opt) === cleanVal(selectedOptionValues[idx])) {
                    score += 1;
                  }
                });

                // Prioritize available variant
                if (variant.available) {
                  score += 0.5;
                }

                if (score > highestScore) {
                  highestScore = score;
                  bestCandidate = variant;
                }
              });

              if (bestCandidate) {
                matchedVariant = bestCandidate;

                // Update DOM inputs to match the best candidate options
                optionContainers.forEach((container, idx) => {
                  const targetValue = bestCandidate.options[idx];
                  const select = container.querySelector('select');
                  if (select) {
                    select.value = targetValue;
                    Array.from(select.options).forEach(opt => {
                      if (cleanVal(opt.value) === cleanVal(targetValue)) {
                        opt.setAttribute('selected', 'selected');
                        opt.selected = true;
                      } else {
                        opt.removeAttribute('selected');
                        opt.selected = false;
                      }
                    });
                  } else {
                    const radios = container.querySelectorAll('input[type="radio"]');
                    radios.forEach(radio => {
                      if (cleanVal(radio.value) === cleanVal(targetValue)) {
                        radio.checked = true;
                        radio.setAttribute('checked', 'checked');
                      } else {
                        radio.checked = false;
                        radio.removeAttribute('checked');
                      }
                    });
                  }

                  // Update visible legend/label text
                  const labelSpan = container.querySelector('[data-selected-value]');
                  if (labelSpan) {
                    labelSpan.textContent = targetValue;
                  }
                });
              }
            }
          }

          if (matchedVariant) {
            console.log('[Variant Lookup] Selected options:', matchedVariant.options);
            console.log('[Variant Lookup] Matched variant ID:', matchedVariant.id);
            console.log('[Variant Lookup] Variant availability:', matchedVariant.available ? 'Available' : 'Sold Out');
            return matchedVariant;
          } else {
            console.log('[Variant Lookup] Reason if no variant is found: No matching variant exists in Shopify for raw selected options:', selectedOptionValues);
            return null;
          }
        } catch (e) {
          console.error('[Variant Lookup] Error matching variant:', e);
          return null;
        }
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        const matchedVariant = this.getMatchingVariantFromDOM();
        if (matchedVariant && matchedVariant.id) {
          params.push(`variant=${matchedVariant.id}`);
        }

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          this.updateVariantDescription(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          // Filter media gallery when variant updates based on Color
          if (window.ProductGalleryColorFilter) {
            window.ProductGalleryColorFilter.update(null, variant);
          }
        };
      }

      updateVariantDescription(variant) {
        if (!variant || !variant.id) return;
        const jsonScript = this.querySelector(`script[id^="VariantDescriptions-"]`) || document.querySelector(`script[id^="VariantDescriptions-"]`);
        if (!jsonScript) return;
        try {
          const descriptionsMap = JSON.parse(jsonScript.textContent);
          const descContainer = this.querySelector(`[id^="ProductDescription-"]`) || document.querySelector(`[id^="ProductDescription-"]`);
          if (descContainer && descriptionsMap[variant.id] !== undefined) {
            descContainer.innerHTML = descriptionsMap[variant.id];
          }
        } catch (e) {
          console.error('Error updating variant product description:', e);
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      getPackOptionValue() {
        // Dropdown select
        const select = this.querySelector('select[name^="options[Pack]"], select[name^="options[pack]"]');
        if (select) {
          return select.value.trim();
        }

        // Radio button / Swatch input in fieldsets
        const fieldsets = this.querySelectorAll('fieldset');
        for (const fieldset of fieldsets) {
          const legend = fieldset.querySelector('legend');
          if (legend && legend.textContent.toLowerCase().includes('pack')) {
            const checkedInput = fieldset.querySelector('input:checked');
            if (checkedInput) {
              return checkedInput.value.trim();
            }
          }
        }

        // Fallback: search inputs directly by name starting with pack
        const packInput = this.querySelector('input[name^="Pack-"]:checked, input[name^="pack-"]:checked');
        if (packInput) {
          return packInput.value.trim();
        }

        return null;
      }

      filterMediaByVariantTag(packValue) {
        if (!packValue) return;

        const mediaGallery = this.closest('.product')?.querySelector('media-gallery') || document.getElementById(`MediaGallery-${this.sectionId}`);
        if (!mediaGallery) return;

        const mainList = mediaGallery.querySelector('.product__media-list');
        const thumbnailList = mediaGallery.querySelector('.thumbnail-list');
        if (!mainList) return;

        const mainGalleryItems = mainList.querySelectorAll('.product__media-item');
        const thumbnailItems = thumbnailList ? thumbnailList.querySelectorAll('.thumbnail-list__item') : [];

        // 1. Assign original index on load to preserve original relative order
        mainGalleryItems.forEach((item, index) => {
          if (!item.hasAttribute('data-original-index')) {
            item.setAttribute('data-original-index', index);
          }
        });
        thumbnailItems.forEach((item, index) => {
          if (!item.hasAttribute('data-original-index')) {
            item.setAttribute('data-original-index', index);
          }
        });

        // Ensure everything is visible (there is no more hidden bucket)
        mainGalleryItems.forEach((item) => item.classList.remove('hidden'));
        thumbnailItems.forEach((item) => {
          item.classList.remove('hidden');
          const button = item.querySelector('.thumbnail');
          if (button) button.classList.remove('hidden');
        });

        const packValueNormalized = packValue.trim().toLowerCase();
        console.log(`[VariantFilter] filterMediaByVariantTag called with packValue: "${packValue}" (normalized: "${packValueNormalized}")`);

        // Helper to partition and reorder children of a list container
        const reorderList = (parentEl, listItems) => {
          const matches = [];
          const commons = [];

          listItems.forEach((item) => {
            let tag = '';
            let label = '';
            if (item.classList.contains('product__media-item')) {
              tag = item.getAttribute('data-variant-tag')?.trim().toLowerCase() || '';
              label = `Slide [ID: ${item.id}]`;
            } else {
              const button = item.querySelector('.thumbnail');
              tag = button?.getAttribute('data-variant-tag')?.trim().toLowerCase() || '';
              label = `Thumbnail [Target: ${item.dataset.target}]`;
            }

            const isMatch = (tag === packValueNormalized);
            if (isMatch) {
              matches.push({ item, originalIndex: parseInt(item.getAttribute('data-original-index')) });
              console.log(`[VariantFilter] ${label} | Tag: "${tag}" | Bucket: match`);
            } else {
              commons.push({ item, originalIndex: parseInt(item.getAttribute('data-original-index')) });
              console.log(`[VariantFilter] ${label} | Tag: "${tag}" | Bucket: common`);
            }
          });

          // Sort each group to preserve original relative order
          matches.sort((a, b) => a.originalIndex - b.originalIndex);
          commons.sort((a, b) => a.originalIndex - b.originalIndex);

          // Append nodes back in reordered sequence: matches first, then commons
          const orderedItems = [...matches, ...commons];
          orderedItems.forEach(({ item }) => {
            parentEl.appendChild(item);
          });
        };

        // 2. Perform DOM reordering for main gallery list and thumbnail list
        reorderList(mainList, mainGalleryItems);
        if (thumbnailList && thumbnailItems.length > 0) {
          reorderList(thumbnailList, thumbnailItems);
        }

        // 3. Re-index slider pages using internal methods so dots/navigation adapt
        if (mediaGallery.elements.viewer && typeof mediaGallery.elements.viewer.resetPages === 'function') {
          mediaGallery.elements.viewer.resetPages();
        }
        if (mediaGallery.elements.thumbnails && typeof mediaGallery.elements.thumbnails.resetPages === 'function') {
          mediaGallery.elements.thumbnails.resetPages();
        }

        // 4. Verify currently active slide and align to the first match if current is not a match
        const activeSlide = mediaGallery.querySelector('.product__media-item.is-active');
        let isActiveValid = false;
        if (activeSlide) {
          const activeTag = activeSlide.getAttribute('data-variant-tag')?.trim().toLowerCase() || '';
          if (activeTag === packValueNormalized) {
            isActiveValid = true;
          }
        }

        if (!isActiveValid) {
          const firstMatchSlide = Array.from(mainList.querySelectorAll('.product__media-item')).find(item => {
            const tag = item.getAttribute('data-variant-tag')?.trim().toLowerCase() || '';
            return tag === packValueNormalized;
          });

          if (firstMatchSlide) {
            const mediaId = firstMatchSlide.getAttribute('data-media-id');
            if (typeof mediaGallery.setActiveMedia === 'function') {
              mediaGallery.setActiveMedia(mediaId, false);
            }
          }
        }
      }

      showAllMedia() {
        const mediaGallery = this.closest('.product')?.querySelector('media-gallery') || document.getElementById(`MediaGallery-${this.sectionId}`);
        if (!mediaGallery) return;

        const mainList = mediaGallery.querySelector('.product__media-list');
        const thumbnailList = mediaGallery.querySelector('.thumbnail-list');
        if (!mainList) return;

        const mainGalleryItems = mainList.querySelectorAll('.product__media-item');
        const thumbnailItems = thumbnailList ? thumbnailList.querySelectorAll('.thumbnail-list__item') : [];

        // Reset visibility
        mainGalleryItems.forEach((item) => item.classList.remove('hidden'));
        thumbnailItems.forEach((item) => {
          item.classList.remove('hidden');
          const button = item.querySelector('.thumbnail');
          if (button) button.classList.remove('hidden');
        });

        // Restore original order
        const restoreOrder = (parentEl, listItems) => {
          const sorted = Array.from(listItems).map(item => ({
            item,
            originalIndex: parseInt(item.getAttribute('data-original-index') || '0')
          })).sort((a, b) => a.originalIndex - b.originalIndex);

          sorted.forEach(({ item }) => {
            parentEl.appendChild(item);
          });
        };

        restoreOrder(mainList, mainGalleryItems);
        if (thumbnailList && thumbnailItems.length > 0) {
          restoreOrder(thumbnailList, thumbnailItems);
        }

        if (mediaGallery.elements.viewer && typeof mediaGallery.elements.viewer.resetPages === 'function') {
          mediaGallery.elements.viewer.resetPages();
        }
        if (mediaGallery.elements.thumbnails && typeof mediaGallery.elements.thumbnails.resetPages === 'function') {
          mediaGallery.elements.thumbnails.resetPages();
        }
      }
    }
  );
}
